import { compose, isEmpty, join, map, toPairs } from 'lodash/fp';
import PropTypes from 'prop-types';
import AlertBox from '../../../components/AlertBox';
import PageStaticSection from '../../../components/PageStaticSection';
import { get, getWithCount } from '../../../services/Pluralize';
import { enumerateRoles, isSuperUser } from '../../../services/User';
import AdminLinkButton from './AdminLinkButton';
import styles from './style.m.less';

AdminOverview.propTypes = {
    categories: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
};

export default function AdminOverview({ currentUser, categories, errorCount }) {
    const hasDisabledLinks = Object.values(categories).some(category => category.some(link => !link.enabled));
    const hasOnlyOneCategory = Object.keys(categories).length === 1;
    return (
        <>
            <PageStaticSection>
                {errorCount > 0 && (
                    <AlertBox colour="RED">
                        <ErrorSummary count={errorCount} />
                    </AlertBox>
                )}
                {isSuperUser(currentUser) ||
                    (hasDisabledLinks && (
                        <AlertBox colour="YELLOW">
                            <RoleWarning user={currentUser} />
                        </AlertBox>
                    ))}
            </PageStaticSection>
            {compose(
                map(([title, links]) => (
                    <AdminOverviewCategory
                        links={links}
                        title={title}
                        key={title}
                        currentUser={currentUser}
                        omitSectionHeader={hasOnlyOneCategory}
                    />
                )),
                toPairs
            )(categories)}
        </>
    );
}

function AdminOverviewCategory({ title, links, currentUser, omitSectionHeader }) {
    return (
        <PageStaticSection className={styles.section}>
            {!omitSectionHeader && <h1 className={styles.sectionHeading}>{title}</h1>}
            <div className={styles.gridContainer}>
                {map(
                    link => (
                        <AdminLinkButton
                            {...link}
                            key={link.id}
                            target={link.category === 'Promotions & Reviews' ? '_blank' : ''}
                            currentUser={currentUser}
                        />
                    ),
                    links
                )}
            </div>
        </PageStaticSection>
    );
}

function ErrorSummary({ count }) {
    return (
        <p>
            There {get('is', count, 'are')} {getWithCount('section', count)} with errors on this Administration page.
            Please review all highlighted sections below in order to avoid unexpected issues.
        </p>
    );
}

function RoleWarning({ user }) {
    const roles = compose(
        join(', '),
        map(r => r.name),
        enumerateRoles
    )(user);
    return (
        <p>
            <strong>Please note:</strong>&nbsp;Due to your user permissions{!isEmpty(roles) && ` (${roles})`} you cannot
            access all options.{' '}
            <a href="https://intercomdocs.small-improvements.com/en/articles/9146183-hr-admins-tech-admins-and-hr-assistants#h_7859d735ca">
                Learn more about SI roles and permissions
            </a>
            .
        </p>
    );
}
